import { useEffect, useRef, useState } from "react";
import { ImageIcon, X, ChevronLeft, ChevronRight } from "lucide-react";

// Real photos uploaded by the client. Items still flagged `src: null` show a
// branded placeholder until the corresponding photo is provided.
const PHOTOS = [
  {
    alt: "Camions Big Déménagement devant la Tour Eiffel à Paris",
    src: "/images/camions-tour-eiffel.webp",
  },
  {
    alt: "Mobilier volumineux soigneusement emballé et sanglé par Big Déménagement avant transport",
    src: "/images/protection-mobilier-demenagement-idf.webp",
  },
  {
    alt: "Penderie carton et cartons Big Déménagement dans un appartement parisien haussmannien",
    src: "/images/cartons-big-demenagement.webp",
  },
  {
    alt: "Fauteuils et canapés protégés par housses et sangles par Big Déménagement",
    src: "/images/fauteuils-proteges-emballage-demenagement.webp",
  },
  {
    alt: "Camion Big Déménagement avec monte-meuble en intervention à Paris",
    src: "/images/camion-monte-meuble.webp",
  },
  {
    alt: "Garde-meuble Big Déménagement : mobilier emballé et stocké en sécurité",
    src: "/images/emballage-mobilier-demenagement-appartement.webp",
  },
  {
    alt: "Déménageur Big Déménagement souriant devant la Tour Eiffel avec son camion et ses cartons",
    src: "/images/eiffel-big-demenagement.webp",
  },
  {
    alt: "Cartons et matériel d'emballage Big Déménagement prêts pour un déménagement",
    src: "/images/famille-demenagement.webp",
  },
  {
    alt: "Flotte de camions Big Déménagement alignés, prête pour les interventions à Paris et en Île-de-France",
    src: "/images/camions-demenagement.webp",
  },
  {
    alt: "Cartons Big Déménagement empilés dans un bureau lors d'un transfert d'entreprise à Paris",
    src: "/images/carton-bureau-big-dem.webp",
  },
  {
    alt: "Déménageur Big Déménagement protégeant un meuble avec film noir et sangles avant transport",
    src: "/images/emballage-meuble-film-big-dem.webp",
  },
  {
    alt: "Déménageur Big Déménagement emballant une armoire haute avec film de protection",
    src: "/images/armoire-emballee-big-dem.webp",
  },
];

const AUTOPLAY_MS = 2000;

export default function Galerie() {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const total = PHOTOS.length;
  const timerRef = useRef(null);
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);

  // Autoplay
  useEffect(() => {
    if (paused || lightbox) return;
    timerRef.current = setInterval(() => {
      setActive((i) => (i + 1) % total);
    }, AUTOPLAY_MS);
    return () => clearInterval(timerRef.current);
  }, [paused, lightbox, total]);

  const go = (dir) => {
    setActive((i) => (i + dir + total) % total);
  };

  const onCardClick = (i, p) => {
    if (i !== active) {
      setActive(i);
      return;
    }
    if (p.src) setLightbox(p);
  };

  // Compute relative offset from active for each card (handles wrap-around).
  const offsetOf = (i) => {
    const half = Math.floor(total / 2);
    let d = i - active;
    if (d > half) d -= total;
    if (d < -half) d += total;
    return d;
  };

  // Swipe handlers (mobile)
  const onTouchStart = (e) => {
    const t = e.touches[0];
    touchStartX.current = t.clientX;
    touchStartY.current = t.clientY;
    setPaused(true);
  };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - touchStartX.current;
    const dy = t.clientY - (touchStartY.current ?? 0);
    touchStartX.current = null;
    touchStartY.current = null;
    setPaused(false);
    // Horizontal swipe only, threshold 40px
    if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
      go(dx < 0 ? 1 : -1);
    }
  };

  return (
    <section
      id="galerie"
      data-testid="galerie-section"
      className="py-10 sm:py-14 bg-[#f7fafc] border-y border-[#e6edf3]"
    >
      <div className="bd-container relative">
        {/* Header + nav */}
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div className="max-w-2xl">
            <span className="bd-eyebrow">Notre travail</span>
            <h2 className="bd-h2 mt-4 text-[26px] sm:text-[30px] lg:text-[36px]">
              Galerie <span style={{ color: "#237cb5" }}>Big Déménagement</span>
            </h2>
            <p className="mt-4 text-[#3a4a5a] text-base leading-relaxed">
              Nos interventions partout en Île-de-France : emballage, protection, transferts de
              bureaux et déménagements particuliers.
            </p>
          </div>

          <div className="hidden sm:flex items-center gap-2 self-start sm:self-end">
            <button
              type="button"
              aria-label="Photo précédente"
              data-testid="galerie-prev"
              onClick={() => go(-1)}
              className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-[#cfdbe6] bg-white text-[#237cb5] hover:bg-[#eaf3fb] hover:border-[#237cb5] transition-colors shadow-sm"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              type="button"
              aria-label="Photo suivante"
              data-testid="galerie-next"
              onClick={() => go(1)}
              className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-[#cfdbe6] bg-white text-[#237cb5] hover:bg-[#eaf3fb] hover:border-[#237cb5] transition-colors shadow-sm"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Coverflow stage */}
        <div
          data-testid="galerie-stage"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
          className="relative mt-10 h-[420px] sm:h-[480px] lg:h-[520px] overflow-hidden"
          style={{ perspective: "1400px" }}
        >
          {PHOTOS.map((p, i) => {
            const off = offsetOf(i);
            const abs = Math.abs(off);
            const isActive = off === 0;
            // Hide cards too far from the active one
            const visible = abs <= 2;

            // Translate further out as |off| grows; scale down side cards.
            const translatePct = off * 70; // % of card width — disperse side cards
            const scale = isActive ? 1 : abs === 1 ? 0.7 : 0.5;
            const opacity = visible ? (isActive ? 1 : abs === 1 ? 0.75 : 0.35) : 0;
            const z = 100 - abs;

            return (
              <button
                key={i}
                type="button"
                data-testid={`galerie-item-${i}`}
                aria-current={isActive}
                aria-hidden={!visible}
                tabIndex={visible ? 0 : -1}
                onClick={() => onCardClick(i, p)}
                className={`group absolute top-1/2 left-1/2 w-[68%] sm:w-[44%] md:w-[36%] lg:w-[30%] aspect-[4/5] rounded-2xl overflow-hidden border border-[#e6edf3] bg-gradient-to-br from-[#eaf3fb] via-white to-[#eaf3fb] transition-[transform,box-shadow,border-color,background-color,color] duration-700 ease-out ${
                  isActive ? "shadow-[0_30px_60px_-20px_rgba(35,124,181,0.45)]" : "shadow-md"
                } ${p.src && isActive ? "cursor-zoom-in" : "cursor-pointer"}`}
                style={{
                  transform: `translate(-50%, -50%) translateX(${translatePct}%) scale(${scale})`,
                  opacity,
                  zIndex: z,
                  pointerEvents: visible ? "auto" : "none",
                }}
              >
                {p.src ? (
                  <img
                    src={p.src}
                    alt={p.alt}
                    loading="lazy"
                    decoding="async"
                    className="absolute inset-0 h-full w-full object-cover"
                  />
                ) : (
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-4 select-none">
                    <span
                      className="inline-flex h-12 w-12 items-center justify-center rounded-xl text-white shadow-md"
                      style={{ background: "linear-gradient(135deg,#237cb5,#5793d8)" }}
                      aria-hidden="true"
                    >
                      <ImageIcon className="h-5 w-5" />
                    </span>
                    <p className="mt-3 text-[10px] font-bold tracking-[0.18em] uppercase text-[#237cb5]">
                      Photo {i + 1}
                    </p>
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* Dots */}
        <div className="mt-6 flex items-center justify-center gap-2">
          {PHOTOS.map((_, i) => (
            <button
              key={i}
              type="button"
              aria-label={`Aller à la photo ${i + 1}`}
              data-testid={`galerie-dot-${i}`}
              onClick={() => setActive(i)}
              className={`h-1.5 rounded-full transition-[transform,box-shadow,border-color,background-color,color] duration-300 ${
                i === active ? "w-8 bg-[#237cb5]" : "w-2 bg-[#cfdbe6] hover:bg-[#9bb6cf]"
              }`}
            />
          ))}
        </div>
      </div>

      {lightbox && (
        <div
          data-testid="galerie-lightbox"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 sm:p-8"
          onClick={() => setLightbox(null)}
        >
          <button
            type="button"
            aria-label="Fermer"
            className="absolute top-5 right-5 inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
            onClick={() => setLightbox(null)}
          >
            <X className="h-5 w-5" />
          </button>
          <img
            src={lightbox.src}
            alt={lightbox.alt}
            className="max-h-full max-w-full rounded-xl shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </section>
  );
}
